import { CartListGroupProps } from './CartList.types'
import {
  CartListGroupHeader,
  CartListGroupHeaderSide,
  CartListGroupHeading,
  CartListGroupIcon,
  CartListGroupProducts,
  CartListGroupTotal,
  CartListGroupWrapper
} from './CartList.styles'
import { getCartLabels } from '../../../utils/getCartLabels'
import Grid from '../Grid'
import ItemCard, { ItemCardProps } from '../ItemCard'
import useId from '../../hooks/useId'

const CartListGroup = ({
  items,
  groupBy,
  subtotal,
  total,
  visibleTotal,
  itemCard = {},
  price,
  groupUnitsLabel = '{count} productos',
  groupUnitsLabelSingular = '{count} producto',
  labelMap,
  groupHeading,
  groupIcon,
  groupTotal,
  groupSubtotal,
  groupUnits,
  'data-testid': testid = 'cart-group'
}: CartListGroupProps) => {
  const { icon, label } = getCartLabels(groupBy, labelMap)
  const totalDisplay = visibleTotal ?? subtotal ?? total
  const { getTestId } = useId(testid)
  const groupUnitsLabelText =
    items?.length === 1 ? groupUnitsLabelSingular : groupUnitsLabel
  return (
    <CartListGroupWrapper className="CartListGroup" data-testid={getTestId()}>
      {label ? (
        <CartListGroupHeader
          className="CartListGroupHeader"
          data-testid={getTestId('header')}>
          <CartListGroupHeaderSide className="CartListGroupHeaderSide">
            <div className="CartListGroupHeaderIcon">
              <CartListGroupIcon
                {...groupIcon}
                name={icon}
                data-testid={getTestId('icon')}
              />
            </div>
            <div className="CartListGroupHeaderHeading">
              <CartListGroupHeading
                {...groupHeading}
                value={label}
                data-testid={getTestId('heading')}
              />
              <CartListGroupProducts
                {...groupUnits}
                data-testid={getTestId('products')}
                value={groupUnitsLabelText.replace(
                  '{count}',
                  String(items?.length)
                )}
              />
            </div>
          </CartListGroupHeaderSide>
          <CartListGroupHeaderSide className="CartListGroupHeaderSide">
            <CartListGroupTotal
              {...price}
              {...groupTotal}
              {...groupSubtotal}
              value={totalDisplay}
              data-testid={getTestId('total')}
            />
          </CartListGroupHeaderSide>
        </CartListGroupHeader>
      ) : null}
      <Grid gap={8} className="CartListGroupGrid">
        {items?.map((item, index) => {
          const itemKey = item.id || item.sku || `item-${index}`
          const isOnCart = item.subItems && !!item.subItems.length
          if (isOnCart) {
            const itemCardProps: Partial<ItemCardProps> = {
              ...itemCard,
              isOnCart,
              body: {
                isOnCart,
                align: 'left',
                fields: [
                  'name',
                  'promotionTag',
                  'promotionSavingLabel',
                  'productOptionsList'
                ]
              }
            }
            return item.subItems?.map((current) => {
              const subItemsProduct = item.subItems?.length
                ? [...item.subItems]
                : []
              const newItemData = {
                ...item,
                quantity: current.quantity,
                subItemsProduct,
                subItem: current
              }
              return (
                <ItemCard
                  key={current.hash}
                  item={newItemData}
                  data-testid={getTestId(itemKey)}
                  {...itemCardProps}
                />
              )
            })
          }
          return (
            <ItemCard
              key={itemKey}
              item={item}
              data-testid={getTestId(itemKey)}
              {...itemCard}
            />
          )
        })}
      </Grid>
    </CartListGroupWrapper>
  )
}

export default CartListGroup
